import 'react-toastify/dist/ReactToastify.css';
import React, {
  useState,
  useEffect,
  useContext,
  ElementType,
  useRef,
} from 'react';
import {
  BrowserRouter as Router,
  Route,
  Link,
  Switch,
  useLocation,
} from 'react-router-dom';
import {
  CssBaseline,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Stack,
  Collapse,
  Menu,
  MenuItem,
  useTheme,
  IconButton,
  Box,
  Typography,
  alpha,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { ToastContainer } from 'react-toastify';
import PortalBar from './PortalBar';
import MonitoringHome from '../Monitoring/MonitoringHome';
import OncalldrillHome from '../Oncalldrill/OncalldrillHome';
import MetricsHome from '../Metrics/MetricsHome';
import CustomerHomePage from '../Customer/Home/CustomerHomePage';
import LicenseHomePage from '../../../LicenseModule/Dashboard/Views/HomePage';
import PurchaseOrdersPage from '../../../LicenseModule/PurchaseOrders/Views/PurchaseOrdersPage';
import ManageLicenses from '../../../LicenseModule/Dashboard/Views/LicenseManagementPage';
import LicenseLogPage from '../../../LicenseModule/LicenseLogs/Views/LicenseLogPage';
import ManageModuleLicenses from '../../../LicenseModule/Dashboard/Views/ModuleLicenseManagementPage';
import HealthHome from '../../../features/DashBoard/Home/HealthHome';
import CollaborationRelay from '../Customer/Components/CollaborationRelay';
import TriggerHome from '../Trigger/TriggerHome';
import TrustedClientsHome from '../TrustedClients/Home/TrustedClientsHome';
import DssSiteHome from '../DssSite/DssSiteHome';
import NotFoundPage from './Global/NotFound';
import { globalService } from './Global/GlobalService';
import { PermissionsContext } from '../UIContainer/PermissionsWrapper';
import { Dictionary } from '../../Common/CommonModel';
import ServicesHome from '../Services/ServicesHome';
import ClientSecreteHome from '../ClientSecrete/Home/ClientSecreteHome';
import EditCustomerPage from '../Customer/Components/EditCustomerPage';
import FarmTable from '../ManageFarms/Home/FarmTable';
import FarmTopology from '../Topology/Topology';
import FarmForm from '../ManageFarms/Components/FarmForm';
import EditForm from '../ManageFarms/Components/EditFarm';
import DeleteFarm from '../ManageFarms/Components/DeleteFarm';
import TenantRegistration from '../Tenant/TenantRegistration';
import TenantMove from '../Tenant/TenantMove';
import RemoveCustomer from '../Customer/Components/RemoveCustomer';
import DeployFarm from '../ManageFarms/Components/DeployFarm';
import DeployGlobalComponents from '../AzureDevOps/Components/DeployGlobalComponents';
import { CustomRoute } from './Global/RouteProps';
import Menus, { MenuOptions } from '../../../Routes/Routes';
import * as Icons from '@mui/icons-material';
import {
  hasAccessToModule,
  hasAccessToPage,
} from '../../../Permission/permissionHandler';
import { TourProvider } from '@reactour/tour';
import CloneTenant from '../Customer/Components/CloneTenant';
import CreateCustomerPage from '../Customer/Components/CreateCustomerPage';
import { useWrapperStyle } from '../../../Common/Styles';
import { Permissions } from './Global/GlobalState';
import CustomizationHome from '../Customer/Components/CustomizationHome';
import { styled } from '@mui/system';
import { NoAccess } from '../Common/NoAccess';
import CostDashboard from '../../../features/DashBoard/Home/DashboardHome';

export const MenuIcon = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  borderRadius: 8,
  flexShrink: 0,
  '& svg': { fontSize: '20px' },
}));

export const StyledIcon = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '22px',
    transition: 'color 0.2s ease, transform 0.2s ease',
  },
}));

const HealthHomeProArc = () => <HealthHome product="ProArc Online" />;

function WrapperContent() {
  const { classes } = useWrapperStyle();
  const theme = useTheme();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isProduction, setIsProduction] = useState(false);
  const permissions = useContext(PermissionsContext);
  const [open, setOpen] = React.useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] =
    useState(false);
  const hoverCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleClose = () => {
    hoverCloseRef.current = setTimeout(() => {
      setAnchorEl(null);
      setOpen(null);
    }, 150);
  };

  const cancelClose = () => {
    if (hoverCloseRef.current) {
      clearTimeout(hoverCloseRef.current);
      hoverCloseRef.current = null;
    }
  };

  const location = useLocation();

  const handleOnHoverClick = (
    event: React.MouseEvent<HTMLElement>,
    key: string,
  ) => {
    if (isDrawerOpen) {
      setOpen(open === key ? null : key);
    } else {
      setAnchorEl(event.currentTarget);
      setOpen(key);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    globalService.getCurrentUserPermissions().then((Permissions) => {
      const permissions = JSON.stringify(Permissions);
      localStorage.setItem('userPermissions', permissions);
    });
    isProductionEnv();
  }, []);

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
    setAnchorEl(null);
  };

  const handleNotificationDrawer = () => {
    setIsNotificationDrawerOpen(!isNotificationDrawerOpen);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setOpen(null);
  };

  const isProductionEnv = () => {
    globalService.getPortalConfig().then((info) => {
      info.Environment.toUpperCase() === 'DEV'
        ? setIsProduction(false)
        : setIsProduction(true);
    });
  };

  const loadFirstSubMenuLink = (
    permissions: Dictionary<Permissions>,
    menu: MenuOptions,
    isProduction: boolean,
  ) => {
    const submenu = menu.SubMenu?.find((sm) =>
      hasAccessToModule(permissions, sm.validationFunc, isProduction),
    );

    if (submenu !== undefined) {
      return submenu.link ?? '/';
    }
  };

  const renderExpandedButton = (
    menu: MenuOptions,
    link: string | undefined,
    isActive: boolean,
    isExpanded: boolean,
    activeExpandedSx: object,
  ) => (
    <ListItemButton
      key={menu.key}
      component={Link as ElementType}
      to={link || '#'}
      sx={{
        mx: 1,
        my: 0.25,
        borderRadius: 2,
        minHeight: 44,
        color: 'text.secondary',
        transition: 'background 0.15s, color 0.15s',
        ...activeExpandedSx,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: 1.5,
          color: isActive ? theme.palette.primary.main : 'text.secondary',
        }}
      >
        <MenuIcon>
          <menu.icon />
        </MenuIcon>
      </ListItemIcon>
      <ListItemText
        primary={menu.name}
        slotProps={{
          primary: {
            sx: {
              fontSize: '0.8125rem',
              fontWeight: isActive ? 600 : 500,
              color: isActive ? theme.palette.primary.main : 'text.primary',
              letterSpacing: '-0.01em',
            },
          },
        }}
      />
      {menu.SubMenu && (
        <IconButton
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleOnHoverClick(event, menu.key);
          }}
          size="small"
          sx={{
            color: 'text.secondary',
            p: 0.25,
            '&:hover': { bgcolor: 'transparent' },
          }}
        >
          {isExpanded ? (
            <Icons.ExpandLess sx={{ fontSize: 16 }} />
          ) : (
            <Icons.ExpandMore sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      )}
    </ListItemButton>
  );

  const renderCollapsedButton = (
    menu: MenuOptions,
    link: string | undefined,
    isActive: boolean,
    activeCollapsedSx: object,
  ) => (
    <ListItemButton
      key={menu.key}
      component={Link as ElementType}
      to={link || '#'}
      onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
        if (menu.SubMenu) {
          cancelClose();
          handleOnHoverClick(event, menu.key);
        } else {
          scheduleClose();
        }
      }}
      onMouseLeave={() => {
        scheduleClose();
      }}
      sx={{
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        mx: 0.5,
        my: 0.125,
        px: 0.5,
        py: 0.75,
        borderRadius: 2,
        minHeight: 52,
        color: 'text.secondary',
        transition: 'background 0.15s, color 0.15s',
        ...activeCollapsedSx,
      }}
    >
      <MenuIcon
        sx={
          isActive
            ? { color: theme.palette.primary.main }
            : { color: 'inherit' }
        }
      >
        <menu.icon />
      </MenuIcon>
      <Typography
        component="span"
        sx={{
          fontSize: '0.6rem',
          fontWeight: isActive ? 600 : 500,
          lineHeight: 1.2,
          mt: 0.4,
          maxWidth: 60,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'inherit',
          letterSpacing: '-0.01em',
        }}
      >
        {menu.name.split(' ')[0]}
      </Typography>
    </ListItemButton>
  );

  const renderListItemButton = (menu: MenuOptions) => {
    const link = menu.link
      ? menu.link
      : loadFirstSubMenuLink(permissions, menu, isProduction);
    const isExpanded = open === menu.key;
    // Check if current route matches link. If link is root '/', only match exactly to avoid highlighting root on every page.
    let isActive = false;
    if (link === '/') {
      isActive = location.pathname === '/';
    } else if (link) {
      isActive = location.pathname.startsWith(link);
    }

    const activeCollapsedSx = isActive
      ? {
          bgcolor: alpha(theme.palette.primary.main, 0.15),
          color: theme.palette.primary.main,
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.22),
          },
        }
      : {
          '&:hover': {
            bgcolor: alpha(theme.palette.text.primary, 0.06),
          },
        };

    const activeExpandedSx = isActive
      ? {
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
          borderLeft: `3px solid ${theme.palette.primary.main}`,
          pl: '13px',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.16),
          },
        }
      : {
          borderLeft: '3px solid transparent',
          pl: '13px',
          '&:hover': {
            bgcolor: alpha(theme.palette.text.primary, 0.05),
          },
        };

    if (isDrawerOpen) {
      return renderExpandedButton(
        menu,
        link,
        isActive,
        isExpanded,
        activeExpandedSx,
      );
    } else {
      return renderCollapsedButton(menu, link, isActive, activeCollapsedSx);
    }
  };

  const routes: CustomRoute[] = [
    {
      path: '/',
      exact: true,
      component: HealthHomeProArc,
      name: 'Service Health',
    },
    {
      path: '/monitoring',
      component: MonitoringHome,
      name: 'Monitoring & Notification',
    },
    {
      path: '/cost',
      component: CostDashboard,
      name: 'Cost Intelligence',
    },
    {
      path: '/customer/:customerId/oncalldrill',
      component: OncalldrillHome,
      name: 'On Call Drill',
    },
    {
      path: '/farm/:Subscription/deploymentcenter',
      component: DeployGlobalComponents,
      name: 'Deployment Center',
    },
    {
      path: '/farm',
      exact: true,
      component: FarmTable,
      name: 'Manage Farms',
    },
    { path: '/farm/new', component: FarmForm, name: 'New Farm' },
    {
      path: '/farm/:FarmId/topology',
      component: FarmTopology,
      name: 'Farm Topology',
    },
    { path: '/farm/:FarmId/edit', component: EditForm, name: 'Edit Farm' },
    {
      path: '/farm/delete/:FarmId',
      component: DeleteFarm,
      name: 'Delete Farm',
    },
    {
      path: '/farm/deploy/:FarmId',
      component: DeployFarm,
      name: 'Deploy Farm',
    },
    {
      path: '/storageaccount/:Operation/name/:Name',
      component: hasAccessToPage(
        permissions,
        'StorageAccountOpsPage',
        isProduction,
      ),
      name: 'Storage Account Operation',
    },
    {
      path: '/storageaccount/:Operation',
      component: hasAccessToPage(
        permissions,
        'StorageAccountOpsPage',
        isProduction,
      ),
      name: 'Storage Account Operation',
    },
    {
      path: '/storageaccount',
      component: hasAccessToPage(
        permissions,
        'StorageAccountPage',
        isProduction,
      ),
      name: 'Storage Account',
    },
    {
      path: '/sqlserver/:sqlserver/edit',
      component: hasAccessToPage(permissions, 'SqlServerEdit', isProduction),
      name: 'Edit',
    },
    {
      path: '/sqlserver/:sqlserver/delete',
      component: hasAccessToPage(permissions, 'SqlServerDelete', isProduction),
      name: 'Delete',
    },
    {
      path: '/sqlserver/create',
      component: hasAccessToPage(permissions, 'SqlServerCreate', isProduction),
      name: 'Create',
    },
    {
      path: '/sqlserver',
      component: hasAccessToPage(permissions, 'SqlServerPage', isProduction),
      name: 'Sql Server',
    },
    {
      path: '/accesscontrol',
      component: hasAccessToPage(
        permissions,
        'AccessControlPage',
        isProduction,
      ),
      name: 'Access Control',
    },
    {
      path: '/keyvault/:Operation/key/:KeyName',
      component: hasAccessToPage(permissions, 'SecureKeyOpsPage', isProduction),
      name: 'Secure Key Operation',
    },
    {
      path: '/keyvault/:Operation',
      component: hasAccessToPage(permissions, 'SecureKeyOpsPage', isProduction),
      name: 'Secure Key Operation',
    },
    {
      path: '/keyvault',
      component: hasAccessToPage(permissions, 'KeyVaultPage', isProduction),
      name: 'Manage Secure key',
    },
    {
      path: '/sqlengine',
      component: hasAccessToPage(permissions, 'SqlEnginePage', isProduction),
      name: 'SQL Engine',
    },
    {
      path: '/emailadministration/sendemail',
      component: hasAccessToPage(permissions, 'EmailSendPage', isProduction),
      name: 'Send Email',
    },
    {
      path: '/emailadministration/emailtemplate/create',
      component: hasAccessToPage(
        permissions,
        'CreateTemplatePage',
        isProduction,
      ),
      name: 'Create Template',
    },
    {
      path: '/emailadministration/:name/:product',
      component: hasAccessToPage(permissions, 'EditTemplatePage', isProduction),
      name: 'Edit Template',
    },
    {
      path: '/emailadministration',
      component: hasAccessToPage(
        permissions,
        'EmailTemplatePage',
        isProduction,
      ),
      name: 'Email',
    },
    {
      path: '/customer',
      exact: true,
      component: CustomerHomePage,
      name: 'Customers',
    },
    {
      path: '/customer/:customerId/modules',
      component: hasAccessToPage(permissions, 'ModulePage', isProduction),
      name: 'Modules',
    },
    {
      path: '/customer/:customerId/relay',
      component: CollaborationRelay,
      name: 'Collaboration Relay',
    },
    { path: '/metrics', component: MetricsHome, name: 'Metrics' },
    { path: '/licenses', component: LicenseHomePage, name: 'License' },
    {
      path: '/license/manage/:tenantId',
      component: ManageLicenses,
      name: 'Manage Licenses',
    },
    {
      path: '/license/module/:tenantId',
      component: ManageModuleLicenses,
      name: 'Manage Module Licenses',
    },
    {
      path: '/purchaseorders/:tenantId',
      component: PurchaseOrdersPage,
      name: 'Purchase Order',
    },
    {
      path: '/logs/:tenantId',
      component: LicenseLogPage,
      name: 'License Log Page',
    },
    {
      path: '/services',
      exact: true,
      component: ServicesHome,
      name: 'Services',
    },
    {
      path: '/audits',
      component: hasAccessToPage(permissions, 'AuditPage', isProduction),
      name: 'Audit Trail',
    },
    {
      path: '/customer/:customerId/trigger',
      component: TriggerHome,
      name: 'Trigger',
    },
    {
      path: '/customer/:customerId/customization',
      component: CustomizationHome,
      name: 'Customization',
    },
    {
      path: '/customer/:customerId/trustedclients',
      component: TrustedClientsHome,
      name: 'TrustedClients',
    },
    {
      path: '/customer/:customerId/manageclientsecret',
      component: ClientSecreteHome,
      name: 'Api Key',
    },
    {
      path: '/customer/:customerId/clone',
      component: CloneTenant,
      name: 'Clone Tenant',
    },
    {
      path: '/customer/create',
      component: CreateCustomerPage,
      name: 'Create Customer',
    },
    {
      path: '/customer/:customerId/edit',
      component: EditCustomerPage,
      name: 'Edit Customer',
    },
    {
      path: '/customer/:customerId/createtenant',
      component: TenantRegistration,
      name: 'Tenant Registration',
    },
    {
      path: '/customer/:customerId/movetenant',
      component: TenantMove,
      name: 'Move Tenant',
    },
    {
      path: '/customer/:customerId/delete',
      component: RemoveCustomer,
      name: 'Remove Customer',
    },
    {
      path: '/customer/:customerId/restart',
      component: hasAccessToPage(
        permissions,
        'TenantRestartPage',
        isProduction,
      ),
      name: 'Tenant Restart',
    },
    {
      path: '/customer/trustedclients/:customerId/Client/:ClientID',
      component: TrustedClientsHome,
      name: 'Trusted Clients',
    },
    {
      path: '/customer/:customerId/sites',
      component: DssSiteHome,
      name: 'Dss Sites',
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: NoAccess,
    },
    {
      path: '/SearchService/:Operation/name/:Name',
      component: hasAccessToPage(
        permissions,
        'AzureSearchServiceOpsPage',
        isProduction,
      ),
      name: 'Azure Search Service Operation',
    },
    {
      path: '/SearchService/:Operation',
      component: hasAccessToPage(
        permissions,
        'AzureSearchServiceOpsPage',
        isProduction,
      ),
      name: 'Azure Search Service Operation',
    },
    {
      path: '/SearchService',
      component: hasAccessToPage(
        permissions,
        'AzureSearchServicePage',
        isProduction,
      ),
      name: 'Azure Search Service',
    },
    { path: '*', component: NotFoundPage, name: 'Not Found' },
  ];

  return (
    <div className={classes.root}>
      <CssBaseline />
      <PortalBar
        notificationClick={handleNotificationDrawer}
        drawerOpen={isDrawerOpen}
        clickOpen={handleDrawerOpen}
        routes={routes}
        showProfile={true}
      />
      <Drawer
        anchor="right"
        open={isNotificationDrawerOpen}
        sx={{
          width: useMediaQuery('(max-width:600px)') ? '80vw' : 500,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: useMediaQuery('(max-width:600px)') ? '80vw' : 500,
          },
        }}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={handleNotificationDrawer} size="large">
            <Icons.ChevronLeft />
          </IconButton>
        </div>

        <Stack sx={{ width: '100%', mt: 2 }} spacing={1}></Stack>
        <Stack sx={{ width: '100%' }} spacing={1}></Stack>
      </Drawer>
      <Drawer
        variant={useMediaQuery('(max-width:900px)') ? 'temporary' : 'permanent'}
        open={isDrawerOpen}
        classes={{
          paper: isDrawerOpen ? classes.drawerPaper : classes.drawerPaperClose,
        }}
      >
        {/* ── Drawer Header ──────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isDrawerOpen ? 'space-between' : 'center',
            px: isDrawerOpen ? 2 : 0,
            minHeight: 56,
            flexShrink: 0,
          }}
        >
          {isDrawerOpen && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icons.CloudOutlined
                sx={{ color: 'primary.main', fontSize: 22 }}
              />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                letterSpacing="-0.02em"
                color="text.primary"
                noWrap
              >
                CloudOps
              </Typography>
            </Box>
          )}
          <IconButton
            onClick={isDrawerOpen ? handleDrawerClose : handleDrawerOpen}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {isDrawerOpen ? (
              <Icons.ChevronLeft sx={{ fontSize: 20 }} />
            ) : (
              <Icons.ChevronRight sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Box>

        <Divider />

        {/* ── Nav Items ──────────────────────────────────────────────────── */}
        <List sx={{ pt: 2, pb: 1, px: 0, flexGrow: 1 }} disablePadding>
          {Menus.map(
            (menu) =>
              hasAccessToModule(
                permissions,
                menu.validationFunc,
                isProduction,
              ) && (
                <Box key={menu.key} sx={{ mb: 2 }}>
                  {renderListItemButton(menu)}

                  {/* ── Expanded: inline collapse ───────────────────────── */}
                  {isDrawerOpen && menu.SubMenu && (
                    <Collapse
                      in={open === menu.key}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {menu.SubMenu.map(
                          (subMenu) =>
                            hasAccessToModule(
                              permissions,
                              subMenu.validationFunc,
                              isProduction,
                            ) && (
                              <ListItemButton
                                key={subMenu.key}
                                component={Link as ElementType}
                                to={subMenu.link ? subMenu.link : '#'}
                                selected={
                                  location.pathname ===
                                  (subMenu.link ? subMenu.link : '#')
                                }
                                sx={{
                                  mx: 1,
                                  my: 0.125,
                                  pl: '32px',
                                  borderRadius: 2,
                                  minHeight: 36,
                                  color: 'text.secondary',
                                  transition: 'background 0.15s, color 0.15s',
                                  '&.Mui-selected': {
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1,
                                    ),
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.16,
                                      ),
                                    },
                                  },
                                  '&:hover': {
                                    bgcolor: alpha(
                                      theme.palette.text.primary,
                                      0.05,
                                    ),
                                  },
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: 1.5,
                                    color: 'inherit',
                                    '& svg': { fontSize: '16px' },
                                  }}
                                >
                                  <subMenu.icon />
                                </ListItemIcon>
                                <ListItemText
                                  primary={subMenu.name}
                                  slotProps={{
                                    primary: {
                                      sx: {
                                        fontSize: '0.78rem',
                                        fontWeight: 500,
                                        color: 'inherit',
                                      },
                                    },
                                  }}
                                />
                              </ListItemButton>
                            ),
                        )}
                      </List>
                    </Collapse>
                  )}

                  {/* ── Collapsed: hover popover ─────────────────────────── */}
                  {!isDrawerOpen && menu.SubMenu && (
                    <Menu
                      anchorEl={anchorEl}
                      open={menuOpen && open === menu.key}
                      onClose={handleClose}
                      slotProps={{
                        root: {
                          // Don't block pointer events on sidebar items behind the menu
                          sx: { pointerEvents: 'none' },
                        },
                        paper: {
                          onMouseEnter: cancelClose,
                          onMouseLeave: scheduleClose,
                          sx: {
                            pointerEvents: 'auto',
                            minWidth: 200,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
                            ml: '4px',
                          },
                        },
                      }}
                      elevation={0}
                      transformOrigin={{
                        horizontal: 'left',
                        vertical: 'top',
                      }}
                      anchorOrigin={{
                        horizontal: 'right',
                        vertical: 'top',
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 0.75,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                          sx={{
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                          }}
                        >
                          {menu.name}
                        </Typography>
                      </Box>
                      {menu.SubMenu.map(
                        (subMenu) =>
                          hasAccessToModule(
                            permissions,
                            subMenu.validationFunc,
                            isProduction,
                          ) && (
                            <MenuItem
                              key={subMenu.key}
                              component={Link as ElementType}
                              to={subMenu.link ? subMenu.link : '#'}
                              selected={
                                location.pathname ===
                                (subMenu.link ? subMenu.link : '#')
                              }
                              onClick={handleClose}
                              sx={{
                                borderRadius: 1.5,
                                mx: 0.75,
                                my: 0.25,
                                px: 1.25,
                                minHeight: 36,
                                gap: 1.5,
                                '&.Mui-selected': {
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.1,
                                  ),
                                  color: theme.palette.primary.main,
                                },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 0,
                                  color: 'inherit',
                                  '& svg': { fontSize: '16px' },
                                }}
                              >
                                <subMenu.icon />
                              </ListItemIcon>
                              <ListItemText
                                primary={subMenu.name}
                                slotProps={{
                                  primary: {
                                    sx: {
                                      fontSize: '0.8rem',
                                      fontWeight: 500,
                                    },
                                  },
                                }}
                              />
                            </MenuItem>
                          ),
                      )}
                    </Menu>
                  )}
                </Box>
              ),
          )}
        </List>
      </Drawer>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <div>
          <Switch>
            {routes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                component={route.component}
                exact={route.exact || false}
              />
            ))}
          </Switch>
        </div>
      </main>
      <ToastContainer pauseOnFocusLoss={false} position="bottom-left" />
    </div>
  );
}

export default function Wrapper() {
  const theme = useTheme();
  return (
    <Router>
      <TourProvider
        steps={[]}
        disableKeyboardNavigation={['esc']}
        defaultOpen={false}
        position={'center'}
        styles={{
          popover: (base) => ({
            ...base,
            borderRadius: 10,
            background: theme.palette.mode === 'dark' ? '#121415' : '#fff',
            color: theme.palette.mode === 'dark' ? '#fafafa' : '#212B36',
            fontSize: '1rem',
            maxWidth: '90vw',
          }),
        }}
      >
        <WrapperContent />
      </TourProvider>
    </Router>
  );
}
